// MUI
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";

// Third Party
import moment from "moment";

//Helpers
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { generateRenderingFn } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";

const BASE_COLUMN_WIDTH = 100;

const createDollarRenderCell = () => {
	return (params: GridRenderCellParams) => {
		return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />;
	};
};

const createPercentageRenderCell = (customFooterValue: string) => {
	return generateRenderingFn(
		{ hints: { isMoney: false, noFocus: true } },
		{
			customFooterValue,
			customFormatFn: (param) => (typeof param.value === "number" ? `${param.value.toFixed(2)}%` : param.value),
		}
	);
};

const createCustomRenderCell = () => {
	return generateRenderingFn(
		{ hints: { isMoney: false } },
		{
			customFormatFn: (param) => (typeof param.value === "number" ? `${param.value.toFixed(2)}` : param.value),
		}
	);
};

const getDepartmentName = (department: string) => {
	const departmentUpper = department?.toUpperCase();
	switch (departmentUpper) {
		case "B":
			return "Body Shop";
		case "S":
			return "Service";
		case "P":
			return "Parts";
		default:
			return department;
	}
};

const getCustomerPayLaborColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => {
	return [
		{
			...baseColumnDefinition,
			field: "customerPayLaborGrossPercentage",
			headerName: "CP Labor Gross (%)",
			width: 80,
			valueGetter: (_, row) => row?.customerPay?.labor?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "customerPay.labor.amount",
			headerName: "CP Labor Gross ($)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			valueGetter: (_, row) => row?.customerPay?.labor?.amount,
			renderHeader: (params) => <TypographyHeader params={params} left={true} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "customerPay.labor.hours",
			headerName: "CP Labor Hours",
			type: "number",
			width: 80,
			valueGetter: (_, row) => row?.customerPay?.labor?.hours,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createCustomRenderCell(),
		},
	];
};

const getWarrantyLaborColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => {
	return [
		{
			...baseColumnDefinition,
			field: "warrantyLaborGrossPercentage",
			headerName: "WARR Labor Gross (%)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.warranty?.labor?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "warranty.labor.amount",
			headerName: "WARR Labor Gross ($)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.warranty?.labor?.amount,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "warranty.labor.hours",
			headerName: "WARR Labor Hours",
			type: "number",
			width: 105,
			renderHeader: (params) => (
				<TypographyHeader params={params} right={true} dangerouslySetInnerHTML={{ __html: "WARR Labor<br />Hours" }}>
					<span dangerouslySetInnerHTML={{ __html: "WARR Labor<br />Hours" }} />
				</TypographyHeader>
			),
			valueGetter: (_, row) => row?.warranty?.labor?.hours,
			renderCell: createCustomRenderCell(),
		},
	];
};

const getInternalLaborColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => {
	return [
		{
			...baseColumnDefinition,
			field: "internalLaborGrossPercentage",
			headerName: "INTL Labor Gross (%)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			align: "right",
			valueGetter: (_, row) => row?.internal?.labor?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "internal.labor.amount",
			headerName: "INTL Labor Gross ($)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.internal?.labor?.amount,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "internal.labor.hours",
			headerName: "INTL Labor Hours",
			type: "number",
			width: 90,
			valueGetter: (_, row) => row?.internal?.labor?.hours,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createCustomRenderCell(),
		},
	];
};

const getPartsColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => {
	return [
		{
			...baseColumnDefinition,
			field: "customerPayPartsGrossPercentage",
			headerName: "CP Parts Gross (%)",
			type: "number",
			width: 85,
			valueGetter: (_, row) => row?.customerPay?.parts?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "customerPay.parts.amount",
			headerName: "CP Parts Gross ($)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.customerPay?.parts?.amount,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "warrantyPartsGrossPercentage",
			headerName: "WARR Parts Gross (%)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			valueGetter: (_, row) => row?.warranty?.parts?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "warranty.parts.amount",
			headerName: "WARR Parts Gross ($)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.warranty?.parts?.amount,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "internalPartsGrossPercentage",
			headerName: "INTL Parts Gross (%)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			valueGetter: (_, row) => row?.internal?.parts?.percentage,
			renderHeader: (params) => <TypographyHeader params={params} right={true} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "internal.parts.amount",
			headerName: "INTL Parts Gross ($)",
			type: "number",
			width: 105,
			valueGetter: (_, row) => row?.internal?.parts?.amount,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
	];
};

export const getColumnDefinitions = (): GridColDef[] => {
	const baseColumnDefinition: Partial<GridColDef> = {
		align: "right",
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
		cellClassName: getCellClassNameFn(),
		renderCell: generateRenderingFn({}, { parentInParens: true }),
		resizable: false,
		type: "string",
	};

	return [
		ID_GRID_COLDEF,
		{
			...baseColumnDefinition,
			field: "department",
			headerName: "Department",
			width: BASE_COLUMN_WIDTH,
			align: "left",
			valueGetter: (_, row) => getDepartmentName(row?.department),
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: (params) => <TypographyCell params={params} />,
		},
		{
			...baseColumnDefinition,
			field: "repairOrderNumber",
			headerName: "Repair Order List",
			width: 84,
			align: "left",
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: (params) => <TypographyCell params={params} />,
		},
		{
			...baseColumnDefinition,
			field: "date",
			headerName: "RO Date",
			type: "date",
			width: 80,
			align: "left",
			renderHeader: (params) => <TypographyHeader params={params} left={true} />,
			valueGetter: (value) => value && getDateInUTC(new Date(moment.utc(value).format(DATE_FORMAT.monthAbbrDate))),
			valueFormatter: (value: string) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => (
				<TypographyCell params={params}>
					{params.value ? getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) : ""}
				</TypographyCell>
			),
		},
		...getCustomerPayLaborColumns(baseColumnDefinition),
		...getWarrantyLaborColumns(baseColumnDefinition),
		...getInternalLaborColumns(baseColumnDefinition),
		...getPartsColumns(baseColumnDefinition),
		{
			...baseColumnDefinition,
			field: "subletGross",
			headerName: "SUBLET GRS ($)",
			type: "number",
			width: 120,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "discountPercentage",
			headerName: "Disc. (%)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createPercentageRenderCell(" "),
		},
		{
			...baseColumnDefinition,
			field: "discountAmount",
			headerName: "Disc. ($)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			renderHeader: (params) => <TypographyHeader params={params} left={true} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "totalGross",
			headerName: "Total ($)",
			type: "number",
			width: 105,
			renderHeader: (params) => <TypographyHeader params={params} left={true} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "repairOrderCount",
			headerName: "RO Count",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: generateRenderingFn(),
		},
		{
			...baseColumnDefinition,
			field: "commission.labor",
			headerName: "Labor Comm ($)",
			type: "number",
			width: 107,
			valueGetter: (_, row) => row?.commission?.labor,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "commission.parts",
			headerName: "Parts Comm ($)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			valueGetter: (_, row) => row?.commission?.parts,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
		{
			...baseColumnDefinition,
			field: "commission.total",
			headerName: "Total Comm ($)",
			type: "number",
			width: BASE_COLUMN_WIDTH,
			valueGetter: (_, row) => row?.commission?.total,
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: createDollarRenderCell(),
		},
	];
};
